import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Card,
  CardContent,
  Rating,
  Stack,
  Container,
  Divider,
  useTheme,
  alpha,
  TextField
} from '@mui/material';
import {
  CleaningServices,
  Schedule,
  Verified,
  Star,
  CheckCircle,
  Speed,
  Security,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SectionBlock from '../components/SectionBlock';
import PageLayout from '../components/PageLayout';
import Seo from '../components/Seo';
import HospitalityStandardSection from '../components/HospitalityStandardSection';
import hospitalityStandards from '../data/hospitalityStandards.json';
import { trackButtonClick } from '../utils/analytics';

const VacationRentals = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [zipCode, setZipCode] = useState('');

  const features = [
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: 'Quick Turnovers',
      description: 'Professional cleaning between guests with guaranteed same-day completion for maximum booking availability.',
    },
    {
      icon: <Star sx={{ fontSize: 40 }} />,
      title: 'Guest Review-Ready',
      description: 'Attention to every detail that impacts guest satisfaction and your property reviews.',
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: 'Trusted Professionals',
      description: 'Background-checked, verified cleaners with specialized Airbnb experience.',
    },
  ];


  const benefits = [
    {
      icon: <CheckCircle color="success" />,
      text: 'Same-day turnovers available'
    },
    {
      icon: <CheckCircle color="success" />,
      text: 'Supply restocking included'
    },
    {
      icon: <CheckCircle color="success" />,
      text: '24/7 online booking & management'
    },
    {
      icon: <CheckCircle color="success" />,
      text: 'Photo reports after each clean'
    },
    {
      icon: <CheckCircle color="success" />,
      text: 'Damage & incident reporting'
    },
    {
      icon: <CheckCircle color="success" />,
      text: 'Guest-ready quality guarantee'
    }
  ];

  const testimonials = [
    {
      text: "PrimeShine has transformed my Airbnb business. Quick turnovers and consistently high quality.",
      author: "Sarah Johnson",
      property: "3 Properties in Orlando",
      rating: 5
    },
    {
      text: "My guest reviews improved dramatically. The attention to detail is incredible.",
      author: "Mike Rodriguez",
      property: "Luxury Condo Downtown",
      rating: 5
    }
  ];

  const cleaningServices = [
    {
      category: 'Guest-Ready Essentials',
      tasks: [
        'Fresh linens and towels',
        'Spotless bathrooms',
        'Kitchen deep clean',
        'Floors vacuumed & mopped',
        'All surfaces dusted'
      ]
    },
    {
      category: 'Guest Experience Details',
      tasks: [
        'Welcome amenities setup',
        'Supply restocking',
        'Window & mirror cleaning',
        'Trash removal & fresh liners',
        'Air freshening'
      ]
    },
    {
      category: 'Property Protection',
      tasks: [
        'Damage inspection & reporting',
        'Security check',
        'Appliance functionality test',
        'Lost item inventory',
        'Photo documentation'
      ]
    }
  ];



  return (
    <PageLayout>
      <Seo
        title="Vacation Rental Turnover Cleaning in Orlando & Kissimmee"
        description="Airbnb and vacation-rental turnover cleaning near Disney done to hotel standard: hospital-corner beds, triple-sheeted linens, spec-folded towels and photo-verified guest staging across Orlando, Kissimmee & Davenport."
        path="/vacation-rentals"
        image="https://www.5fairiescleaning.com/media/services/hospitality_bed_top.jpg"
      />
          {/* Hero Section */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
              py: { xs: 8, md: 12 },
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Container maxWidth="lg">
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h2"
                    fontWeight={800}
                    color="primary.main"
                    gutterBottom
                    sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}
                  >
                    Professional Airbnb Cleaning Services
                  </Typography>
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    sx={{ mb: 4, lineHeight: 1.6 }}
                  >
                    Find, book, and manage top-rated Airbnb cleaning professionals. 
                    Same-day turnovers with guest review-ready quality.
                  </Typography>
                  
                  <Stack spacing={2} sx={{ mb: 4 }}>
                    {benefits.slice(0, 3).map((benefit, index) => (
                      <Stack direction="row" alignItems="center" spacing={1} key={index}>
                        {benefit.icon}
                        <Typography variant="body1" fontWeight={500}>
                          {benefit.text}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                  
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button 
                      variant="contained" 
                      size="large" 
                      sx={{ py: 1.5, px: 4 }}
                      onClick={() => navigate('/booking')}
                    >
                      Book Now
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="large" 
                      sx={{ py: 1.5, px: 4 }}
                      onClick={() => navigate('/booking')}
                    >
                      View Services
                    </Button>
                  </Stack>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 20,
                        left: 20,
                        right: -20,
                        bottom: -20,
                        background: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: 3,
                        zIndex: 0
                      }
                    }}
                  >
                    <Paper
                      elevation={24}
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        position: 'relative',
                        zIndex: 1,
                        background: 'white'
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                        <CleaningServices color="primary" sx={{ fontSize: 40 }} />
                        <Box>
                          <Typography variant="h6" fontWeight={600}>
                            Quick Booking
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Same-day service available
                          </Typography>
                        </Box>
                      </Stack>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Stack spacing={3}>
                          <DatePicker
                            label="Select Date"
                            value={selectedDate}
                            onChange={setSelectedDate}
                            renderInput={(params) => <TextField {...params} fullWidth />}
                            minDate={new Date()}
                          />
                          
                          <TextField
                            label="ZIP Code"
                            value={zipCode}
                            onChange={(e) => setZipCode(e.target.value)}
                            fullWidth
                            placeholder="Enter your ZIP code"
                          />
                          
                          <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            fullWidth
                            onClick={() => navigate('/booking')}
                          >
                            Start Booking
                          </Button>
                        </Stack>
                      </LocalizationProvider>
                    </Paper>
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Box>

          {/* Features Section */}
          <SectionBlock 
            title="Why Choose the 5 Fairies?"
            subtitle="Specialized expertise for vacation rental properties"
            bgcolor="alternate.main"
          >
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      p: 4, 
                      textAlign: 'center', 
                      height: '100%',
                      border: '2px solid',
                      borderColor: 'divider',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[8]
                      }
                    }}
                  >
                    <Box sx={{ color: 'primary.main', mb: 3 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </SectionBlock>

          {/* Hospitality Standard — short-term rental turnover technique */}
          <SectionBlock
            maxWidth="lg"
            title={hospitalityStandards.title}
            subtitle={hospitalityStandards.subtitleShort}
          >
            <HospitalityStandardSection
              cta={{
                label: 'Book a Guest-Ready Turnover',
                onClick: () => {
                  trackButtonClick('hospitality_standard_cta', 'vacation_rentals');
                  navigate('/booking');
                },
              }}
            />
          </SectionBlock>

          {/* Hospitality Linen Program cross-link */}
          <SectionBlock maxWidth="lg" py={{ xs: 4, md: 6 }}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 3,
                border: '2px solid',
                borderColor: 'primary.main',
              }}
            >
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Box
                    component="img"
                    src="/media/services/linen_microfiber_bedding_sheets_and_pillow.webp"
                    alt="Hotel bed made with a complete white bedding set and matching pillowcases from the Hospitality Linen Program"
                    loading="lazy"
                    decoding="async"
                    sx={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      objectFit: 'cover',
                      display: 'block',
                      borderRadius: 2,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <Typography variant="overline" color="primary" fontWeight={700}>
                    Hospitality Linen Program · $59/month
                  </Typography>
                  <Typography variant="h4" fontWeight={800} gutterBottom>
                    Hotel-quality linens without the upfront investment
                  </Typography>
                  <Typography color="text.secondary">
                    Professionally managed hospitality-grade linen inventory with three complete
                    rotations, wear replacement included, and linen service built into every
                    turnover. See what owning your own linens really costs — and what you save.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/hospitality-linen')}
                  >
                    Compare the Costs
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </SectionBlock>

          {/* Services Section */}
          <SectionBlock
            bgcolor="alternate.main"
            title="Complete Airbnb Cleaning Services"
            subtitle="Everything you need for guest-ready turnovers"
          >
            <Grid container spacing={4}>
              {cleaningServices.map((service, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card sx={{ height: '100%', borderRadius: 3 }}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h5" fontWeight={600} gutterBottom color="primary.main">
                        {service.category}
                      </Typography>
                      <Stack spacing={1.5}>
                        {service.tasks.map((task, idx) => (
                          <Stack direction="row" alignItems="center" spacing={1} key={idx}>
                            <CheckCircle color="success" sx={{ fontSize: 20 }} />
                            <Typography variant="body2">{task}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </SectionBlock>

          {/* Testimonials */}
          <SectionBlock 
            title="What Property Owners Say"
            subtitle="Join hundreds of satisfied Airbnb hosts"
          >
            <Grid container spacing={4}>
              {testimonials.map((testimonial, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      p: 4, 
                      border: '2px solid',
                      borderColor: 'divider',
                      borderRadius: 3,
                      height: '100%'
                    }}
                  >
                    <Rating value={testimonial.rating} sx={{ mb: 2 }} readOnly />
                    <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic' }}>
                      "{testimonial.text}"
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {testimonial.author}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {testimonial.property}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </SectionBlock>

          {/* How It Works */}
          <SectionBlock 
            bgcolor="alternate.main"
            title="How It Works"
            subtitle="Simple 3-step process to book professional cleaning"
          >
            <Grid container spacing={4}>
              {[
                {
                  step: '1',
                  title: 'Book Online',
                  description: 'Select your property type, preferred date, and cleaning professional',
                  icon: <Schedule sx={{ fontSize: 40 }} />
                },
                {
                  step: '2', 
                  title: 'Professional Clean',
                  description: 'Background-checked cleaner arrives and completes guest-ready service',
                  icon: <CleaningServices sx={{ fontSize: 40 }} />
                },
                {
                  step: '3',
                  title: 'Photo Report',
                  description: 'Receive detailed photos and completion report for your records',
                  icon: <Verified sx={{ fontSize: 40 }} />
                }
              ].map((step, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                        fontSize: '2rem',
                        fontWeight: 600
                      }}
                    >
                      {step.step}
                    </Box>
                    <Typography variant="h5" fontWeight={600} gutterBottom>
                      {step.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {step.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </SectionBlock>

          {/* Final CTA */}
          <SectionBlock id="booking-section">
            <Box
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                borderRadius: 4,
                p: { xs: 4, md: 8 },
                textAlign: 'center',
                color: 'white'
              }}
            >
              <Typography variant="h3" fontWeight={800} gutterBottom>
                Ready to Maximize Your Airbnb Revenue?
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
                Professional cleaning that drives better reviews and higher occupancy rates
              </Typography>
              <Button 
                variant="contained" 
                size="large" 
                onClick={() => navigate('/booking')}
                sx={{ 
                  py: 2, 
                  px: 4,
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: alpha('#fff', 0.9)
                  }
                }}
              >
                Book Your First Clean
              </Button>
            </Box>
          </SectionBlock>
    </PageLayout>
  );
};

export default VacationRentals;